import { Epic, Story, Sprint } from "../types";
import { handleError } from "./errorHandler";

// Jira API Configuration (now client-side only, no credentials)
export interface JiraConfig {
  projectKey: string;
}

export interface JiraApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
  meta?: {
    timestamp: string;
    requestId: string;
  };
}

class SecureJiraApiService {
  private baseUrl: string;

  constructor() {
    // Use the current origin for API calls
    this.baseUrl = window.location.origin;
  }

  /**
   * Makes a secure API request through our server-side proxy
   */
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    context?: Record<string, any>,
  ): Promise<JiraApiResponse<T>> {
    const url = `${this.baseUrl}/api/jira${endpoint}`;
    const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          "X-Request-ID": requestId,
          ...options.headers,
        },
        signal: AbortSignal.timeout(30000), // 30 second timeout
      });

      let result: JiraApiResponse<T>;

      try {
        result = await response.json();
      } catch (parseError) {
        throw new Error(
          `Failed to parse response: ${parseError instanceof Error ? parseError.message : "Unknown error"}`,
        );
      }

      if (!response.ok || !result.success) {
        // Create a structured error with HTTP context
        const httpError = new Error(
          result.error?.message ||
            `HTTP ${response.status}: ${response.statusText}`,
        );

        // Add HTTP status and response info to error
        (httpError as any).status = response.status;
        (httpError as any).statusText = response.statusText;
        (httpError as any).url = url;
        (httpError as any).requestId = requestId;
        (httpError as any).endpoint = endpoint;
        (httpError as any).method = options.method || "GET";
        (httpError as any).responseBody = result;

        throw httpError;
      }

      return result;
    } catch (error) {
      // Handle the error through our centralized error handler
      const appError = handleError(error, {
        type: "api_request",
        endpoint,
        method: options.method || "GET",
        url,
        requestId,
        ...context,
      });

      // Return error response in consistent format
      return {
        success: false,
        error: {
          code: appError.code,
          message: appError.userMessage,
          details: {
            originalError: appError.message,
            requestId,
            endpoint,
            category: appError.category,
            retryable: appError.retryable,
          },
        },
      };
    }
  }

  /**
   * Connect to Jira with basic info (server will handle secure authentication)
   */
  async connect(config: {
    baseUrl: string;
    email: string;
    projectKey: string;
  }): Promise<
    JiraApiResponse<{
      connected: boolean;
      projectKey: string;
      baseUrl: string;
      needsToken: boolean;
    }>
  > {
    return this.makeRequest(
      "/auth/jira-connect",
      {
        method: "POST",
        body: JSON.stringify(config),
      },
      {
        operation: "jira_connect",
        authentication: true,
        projectKey: config.projectKey,
        baseUrl: config.baseUrl,
      },
    );
  }

  /**
   * Submit API token securely (only if requested by server)
   */
  async submitApiToken(
    apiToken: string,
  ): Promise<JiraApiResponse<{ connected: boolean }>> {
    return this.makeRequest("/auth/api-token", {
      method: "POST",
      body: JSON.stringify({ apiToken }),
    });
  }

  /**
   * Disconnect from Jira
   */
  async disconnect(): Promise<JiraApiResponse<{ connected: boolean }>> {
    return this.makeRequest("/auth/jira-disconnect", {
      method: "DELETE",
    });
  }

  /**
   * Get current authentication status
   */
  async getStatus(): Promise<
    JiraApiResponse<{
      connected: boolean;
      project?: { projectKey: string; baseUrl: string; email: string };
    }>
  > {
    return this.makeRequest("/auth/status");
  }

  /**
   * Get all Jira projects
   */
  async getProjects(): Promise<JiraApiResponse> {
    return this.makeRequest("/projects");
  }

  /**
   * Get boards for a project
   */
  async getBoards(projectKey: string): Promise<JiraApiResponse> {
    return this.makeRequest(`/boards/${projectKey}`);
  }

  /**
   * Get all epics for the configured project
   */
  async getEpics(): Promise<JiraApiResponse<Epic[]>> {
    const result = await this.makeRequest(
      "/epics",
      {},
      {
        operation: "get_epics",
        dataType: "epics",
      },
    );
    if (result.success && result.data && Array.isArray(result.data)) {
      // Transform Jira API response to our Epic format
      const transformedEpics = this.transformJiraEpics(result.data);
      return { ...result, data: transformedEpics };
    }
    return { ...result, data: [] };
  }

  /**
   * Get stories for an epic
   */
  async getEpicStories(epicKey: string): Promise<JiraApiResponse<Story[]>> {
    const result = await this.makeRequest(`/epics/${epicKey}/stories`);
    if (result.success && result.data && Array.isArray(result.data)) {
      // Transform Jira API response to our Story format
      const transformedStories = this.transformJiraStories(result.data);
      return { ...result, data: transformedStories };
    }
    return { ...result, data: [] };
  }

  /**
   * Get sprints for a board
   */
  async getSprints(boardId: string): Promise<JiraApiResponse<Sprint[]>> {
    const result = await this.makeRequest(`/boards/${boardId}/sprints`);
    if (result.success && result.data && Array.isArray(result.data)) {
      // Transform Jira API response to our Sprint format
      const transformedSprints = this.transformJiraSprints(result.data);
      return { ...result, data: transformedSprints };
    }
    return { ...result, data: [] };
  }

  /**
   * Get issues for a sprint
   */
  async getSprintIssues(
    boardId: string,
    sprintId: string,
  ): Promise<JiraApiResponse<Story[]>> {
    const result = await this.makeRequest(
      `/boards/${boardId}/sprints/${sprintId}/issues`,
    );
    if (result.success && result.data && Array.isArray(result.data)) {
      // Transform Jira API response to our Story format
      const transformedStories = this.transformJiraStories(result.data);
      return { ...result, data: transformedStories };
    }
    return { ...result, data: [] };
  }

  /**
   * Get worklog for an issue
   */
  async getIssueWorklogs(issueKey: string): Promise<JiraApiResponse> {
    return this.makeRequest(`/issues/${issueKey}/worklogs`);
  }

  /**
   * Log work on an issue
   */
  async logWork(
    issueKey: string,
    timeSpent: string,
    comment?: string,
  ): Promise<JiraApiResponse> {
    return this.makeRequest(
      `/issues/${issueKey}/worklog`,
      {
        method: "POST",
        body: JSON.stringify({ timeSpent, comment }),
      },
      {
        operation: "log_work",
        issueKey,
        timeSpent,
        hasComment: !!comment,
      },
    );
  }

  /**
   * Update issue (for drag-and-drop priority changes)
   */
  async updateIssue(issueKey: string, fields: any): Promise<JiraApiResponse> {
    return this.makeRequest(`/issues/${issueKey}`, {
      method: "PUT",
      body: JSON.stringify({ fields }),
    });
  }

  /**
   * Transition issue status
   */
  async transitionIssue(
    issueKey: string,
    transitionId: string,
  ): Promise<JiraApiResponse> {
    return this.makeRequest(`/issues/${issueKey}/transitions`, {
      method: "POST",
      body: JSON.stringify({ transitionId }),
    });
  }

  // Transformation methods to convert Jira API responses to our internal types

  private transformJiraEpics(jiraEpics: any[]): Epic[] {
    return jiraEpics.map((issue) => ({
      id: issue.id,
      key: issue.key,
      title: issue.fields.summary,
      description: issue.fields.description || "",
      status: this.mapJiraStatus(issue.fields.status.statusCategory.key),
      priority: this.mapJiraPriority(issue.fields.priority.name),
      assignee: issue.fields.assignee?.displayName || "Unassigned",
      storyPoints: issue.fields.customfield_10016 || 0,
      stories: [], // Will be populated separately
      startDate: new Date(issue.fields.created),
      endDate: new Date(), // Calculate based on stories
      color: issue.fields.epic?.color || this.getRandomColor(),
    }));
  }

  private transformJiraStories(jiraStories: any[]): Story[] {
    return jiraStories.map((issue) => ({
      id: issue.id,
      key: issue.key,
      title: issue.fields.summary,
      description: issue.fields.description || "",
      status: this.mapJiraStatus(issue.fields.status.statusCategory.key),
      priority: this.mapJiraPriority(issue.fields.priority.name),
      assignee: issue.fields.assignee?.displayName || "Unassigned",
      storyPoints: issue.fields.customfield_10016 || 0,
      timeSpent: this.parseJiraTime(issue.fields.timetracking?.timeSpent),
      remainingEstimate: this.parseJiraTime(
        issue.fields.timetracking?.remainingEstimate,
      ),
      subtasks: [], // Will be populated separately if needed
      epicId: issue.fields.parent?.key || "",
    }));
  }

  private transformJiraSprints(jiraSprints: any[]): Sprint[] {
    return jiraSprints.map((sprint) => ({
      id: sprint.id.toString(),
      name: sprint.name,
      goal: sprint.goal || "",
      startDate: sprint.startDate ? new Date(sprint.startDate) : new Date(),
      endDate: sprint.endDate ? new Date(sprint.endDate) : new Date(),
      status:
        sprint.state === "closed"
          ? "completed"
          : sprint.state === "active"
            ? "active"
            : "future",
      stories: [], // Will be populated when fetching issues
      velocity: 0, // Will be calculated from completed stories
      totalPoints: 0,
      completedPoints: 0,
    }));
  }

  private mapJiraStatus(
    statusCategory: string,
  ): "todo" | "in-progress" | "done" {
    switch (statusCategory.toLowerCase()) {
      case "done":
        return "done";
      case "indeterminate":
        return "in-progress";
      default:
        return "todo";
    }
  }

  private mapJiraPriority(priority: string): "high" | "medium" | "low" {
    switch (priority.toLowerCase()) {
      case "highest":
      case "high":
        return "high";
      case "medium":
        return "medium";
      default:
        return "low";
    }
  }

  private parseJiraTime(timeString?: string): number {
    if (!timeString) return 0;

    // Parse Jira time format (e.g., "2h 30m", "1d 4h", "45m")
    const hours = timeString.match(/(\d+)h/);
    const minutes = timeString.match(/(\d+)m/);
    const days = timeString.match(/(\d+)d/);

    let totalHours = 0;
    if (days) totalHours += parseInt(days[1]) * 8; // Assuming 8-hour workday
    if (hours) totalHours += parseInt(hours[1]);
    if (minutes) totalHours += parseInt(minutes[1]) / 60;

    return Math.round(totalHours * 100) / 100; // Round to 2 decimal places
  }

  private getRandomColor(): string {
    const colors = [
      "#3B82F6",
      "#8B5CF6",
      "#10B981",
      "#F59E0B",
      "#EF4444",
      "#06B6D4",
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  }
}

export { SecureJiraApiService };
